import React from "react";
import {
  MapPin, Phone, MessageCircle, Instagram, Star, Clock, Facebook,
  Accessibility, Dog, Baby, Music, Wine, CreditCard,
  ParkingCircle, Wifi, Leaf, ShoppingBag, Coffee, Heart,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { T } from "../i18n/translations";
import { CONTACT } from "../data/media";

const ICONS = {
  Accessibility, Dog, Baby, Music, Wine, CreditCard,
  ParkingCircle, Wifi, Leaf, ShoppingBag, Coffee, Heart,
};

export default function Contatti() {
  const { lang } = useLang();
  const c = T.contatti_page;
  return (
    <>
      {/* Header */}
      <section className="relative bg-trise-blue text-trise-ivory pt-36 pb-20 md:pt-44 md:pb-28 grain overflow-hidden" data-testid="contatti-header">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-widen text-trise-brass">— {c.tag[lang]}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.95]">{c.heading[lang]}</h1>
          </Reveal>
        </div>
      </section>

      {/* Info + Hours */}
      <section className="bg-trise-ivory py-20 md:py-28" data-testid="contatti-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14">
          {/* Left: details */}
          <div className="md:col-span-5 flex flex-col gap-10">
            <Reveal>
              <div>
                <h3 className="text-xs uppercase tracking-widen text-trise-terra mb-3">{c.address_label[lang]}</h3>
                <a href={CONTACT.mapEmbed.replace("&output=embed", "")} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-xl md:text-2xl font-light text-trise-ink hover:text-trise-terra transition-colors" data-testid="contatti-address">
                  <MapPin size={22} className="text-trise-brass mt-1 shrink-0" />
                  {CONTACT.address}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div>
                <h3 className="text-xs uppercase tracking-widen text-trise-terra mb-3">{c.phone_label[lang]}</h3>
                <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center gap-3 text-xl md:text-2xl font-light text-trise-ink hover:text-trise-terra transition-colors" data-testid="contatti-phone">
                  <Phone size={20} className="text-trise-brass" /> {CONTACT.phoneDisplay}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <h3 className="text-xs uppercase tracking-widen text-trise-terra mb-4">{c.social_label[lang]}</h3>
                <div className="flex flex-wrap gap-3">
                  <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contatti-instagram" className="inline-flex items-center gap-2 border border-trise-brass/60 text-trise-ink px-5 py-3 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors">
                    <Instagram size={16} /> Instagram
                  </a>
                  <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="contatti-facebook" className="inline-flex items-center gap-2 border border-trise-brass/60 text-trise-ink px-5 py-3 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors">
                    <Facebook size={16} /> Facebook
                  </a>
                  <a href={CONTACT.tripadvisor} target="_blank" rel="noopener noreferrer" data-testid="contatti-tripadvisor" className="inline-flex items-center gap-2 border border-trise-brass/60 text-trise-ink px-5 py-3 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors">
                    <Star size={16} /> TripAdvisor
                  </a>
                  <a href={CONTACT.google} target="_blank" rel="noopener noreferrer" data-testid="contatti-google" className="inline-flex items-center gap-2 border border-trise-brass/60 text-trise-ink px-5 py-3 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors">
                    <MapPin size={16} /> Google
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: hours */}
          <div className="md:col-span-7">
            <Reveal delay={0.05}>
              <div className="border border-trise-brass/40 bg-trise-white p-8 md:p-10" data-testid="hours-card">
                <div className="flex items-center gap-3 mb-6">
                  <Clock size={20} className="text-trise-brass" />
                  <h3 className="text-xs uppercase tracking-widen text-trise-terra">{c.hours_label[lang]}</h3>
                </div>
                <ul>
                  {T.hours.map((d, i) => (
                    <li key={i} className="flex items-center justify-between py-3 border-b border-dotted border-trise-brass/40 last:border-0">
                      <span className="text-base md:text-lg font-medium text-trise-ink">{d[lang]}</span>
                      {d.closed ? (
                        <span className="text-sm uppercase tracking-widen text-trise-terra font-bold">{c.closed[lang]}</span>
                      ) : (
                        <span className="text-sm md:text-base text-trise-ink/70 tabular-nums">{d.slots}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Reserve CTA */}
      <section className="bg-trise-blue text-trise-ivory py-20 md:py-28 grain relative overflow-hidden" data-testid="contatti-reserve">
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight">{c.reserve_heading[lang]}</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-6 text-trise-ivory/75 text-base md:text-lg max-w-xl mx-auto leading-relaxed">{c.reserve_body[lang]}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contatti-whatsapp" className="inline-flex items-center gap-2 bg-trise-brass text-trise-blue px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-terra hover:text-trise-ivory transition-colors duration-300">
                <MessageCircle size={16} /> {c.whatsapp_btn[lang]}
              </a>
              <a href={`tel:${CONTACT.phoneTel}`} data-testid="contatti-call" className="inline-flex items-center gap-2 border border-trise-ivory/60 text-trise-ivory px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-ivory hover:text-trise-blue transition-colors duration-300">
                <Phone size={16} /> {c.call_btn[lang]}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="bg-trise-ivory" data-testid="contatti-map">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <div className="border border-trise-brass/40 overflow-hidden">
              <iframe
                title="Trisé Bistrot — Corso Gelone 99, Siracusa"
                src={CONTACT.mapEmbed}
                className="w-full h-[380px] md:h-[460px]"
                style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="bg-trise-white py-20 md:py-28" data-testid="contatti-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-widen text-trise-terra">— {c.services_tag[lang]}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-trise-ink max-w-2xl">{c.services_heading[lang]}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-trise-brass/25 border border-trise-brass/25">
            {T.services.map((sv, i) => {
              const Icon = ICONS[sv.icon] || Heart;
              return (
                <Reveal key={i} delay={Math.min(i * 0.03, 0.25)} y={20} className="bg-trise-white">
                  <div className="flex flex-col gap-4 p-7 md:p-9 h-full" data-testid={`service-${i}`}>
                    <Icon size={26} className="text-trise-brass" strokeWidth={1.5} />
                    <div>
                      <p className="text-base font-medium text-trise-ink leading-snug">{sv[lang]}</p>
                      <p className="text-xs italic text-trise-ink/50 mt-0.5">{sv[lang === "it" ? "en" : "it"]}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
